// import React, { useState, useEffect } from "react";
import styles from "./styles/JoinGame.module.css";
import useTranslation from "next-translate/useTranslation";
import { convertToNumber, getModeId } from "../../util/util";
import { Invite, setBlinking, useLobby } from "../../redux/lobby";
import { VARIANT_CHESS960, VARIANT_CHESS } from "@chesshotel/constants";
import { FaUser } from "react-icons/fa";
import { FaSignal } from "react-icons/fa";
import { FaClock } from "react-icons/fa";
import { FaChessRook } from "react-icons/fa";
import { FaBan } from "react-icons/fa";
import { FaDiceSix } from "react-icons/fa";
import { FaBolt } from "react-icons/fa";
import { FaDragon } from "react-icons/fa";
import { BulletIcon } from "../../icons/BulletIcon";
import { useSettings } from "../../redux/settings";
import { useUser } from "../../redux/user";
import { LobbyWs } from "../../services/wsOutRoutes";
import { useElo } from "@/hooks/useElo";
import { useWsConnection } from "@/redux/wsConnection";

function getGameType(time: number, variant: number) {
  if (convertToNumber(variant) === VARIANT_CHESS960) {
    return (
      <td>
        <div className="flex items-center space-x-1">
          <FaDiceSix />
          <div>960</div>
        </div>
      </td>
    );
  } else if (time < 3) {
    return (
      <td>
        <div className="flex items-center space-x-1">
          <BulletIcon />
          <div>Bullet</div>
        </div>
      </td>
    );
  } else if (time < 10) {
    return (
      <td>
        <div className="flex items-center space-x-1">
          <FaBolt />
          <div>Blitz</div>
        </div>
      </td>
    );
  } else {
    return (
      <td>
        <div className="flex items-center space-x-1">
          <FaDragon />
          <div>Rapid</div>
        </div>
      </td>
    );
  }
}

function formatTime(time: number, inc: number) {
  let timeStr = "";
  if (!inc) {
    timeStr = time + "m";
  } else {
    timeStr = time + "m + " + inc + "s";
  }
  return timeStr;
}

function getRow(
  invite: Invite,
  maxOpponentRating: number,
  minOpponentRating: number,
  noGuests: boolean,
  leaguePlay: boolean
) {
  const username = invite.isGuest
    ? invite.username.substring(2)
    : invite.username;
  const time = formatTime(invite.time, invite.inc);

  let isEnabled = true;
  const classes = [];
  if (invite.removing) {
    classes.push(styles["removing"]);
    isEnabled = false;
  }

  let isBlocked = false;
  if (
    invite.elo > maxOpponentRating ||
    invite.elo < minOpponentRating ||
    (invite.isGuest && noGuests)
  ) {
    classes.push(styles["removing"]);
    isEnabled = false;
    isBlocked = true;
  }

  if (invite.blink) {
    classes.push(styles["blink"]);
    isEnabled = false;
  }
  const keyId = `${username}${time}${invite.variant}`;

  return (
    <tr
      key={keyId}
      className={classes.join(" ")}
      onClick={() => {
        if (isEnabled) {
          acceptInvite(invite, leaguePlay);
        }
      }}
    >
      {isBlocked ? (
        <td>
          <div className="flex items-center space-x-1">
            <FaBan />
            <div>{username}</div>
          </div>
        </td>
      ) : (
        <td>{username}</td>
      )}
      <td>{invite.elo}</td>
      <td>{time}</td>
      {getGameType(invite.time, invite.variant)}
    </tr>
  );
}

function InvitesTable() {
  const { t } = useTranslation("common");
  const { t: lobbyT } = useTranslation("lobby");
  const { leaguePlay, invites, gamesCreated } = useLobby();
  const { maxOpponentRating, minOpponentRating, noGuests } = useSettings();
  const { displayName } = useUser();
  const getElo = useElo();

  // Separate regular invites and Chess960 invites
  const regularInvites = [];
  const chess960invites = [];

  for (let i = 0; i < invites.length; i++) {
    const invite = invites[i];
    if (invite.variant === VARIANT_CHESS960) {
      chess960invites.push(invite);
    } else {
      regularInvites.push(invite);
    }
  }

  // Build all rows in the correct order
  const allRows = [];

  // Add created games first
  const createdGames = gamesCreated.map((game, index) => {
    const modeId = getModeId(game.time, game.variant, null);
    const gameElo = getElo(leaguePlay, modeId, game.variant);
    const formatedTime = formatTime(game.time, game.inc);

    return (
      <tr
        key={`created-${index}`}
        className={styles.removing}
        style={{ animation: game.blink ? "blink 0.25s 3" : "none" }}
        onAnimationEnd={(e) => {
          if (e.animationName === "blink") {
            setBlinking({
              time: game.time,
              inc: game.inc,
              variant: game.variant,
              blinking: false,
            });
          }
        }}
      >
        <td>{displayName}</td>
        <td>{gameElo}</td>
        <td>{formatedTime}</td>
        {getGameType(game.time, game.variant)}
      </tr>
    );
  });
  allRows.push(...createdGames);

  // Add regular invites
  for (let i = 0; i < regularInvites.length; i++) {
    const invite = regularInvites[i];
    allRows.push(
      getRow(invite, maxOpponentRating, minOpponentRating, noGuests, leaguePlay)
    );
  }

  // Add Chess960 divider and invites at the bottom
  if (chess960invites.length > 0) {
    allRows.push(
      <tr key={"variant"}>
        <td colSpan={4} className={styles.variantDivider}>
          {"Chess960 games"}
        </td>
      </tr>
    );
    for (let i = 0; i < chess960invites.length; i++) {
      const chess960invite = chess960invites[i];
      allRows.push(
        getRow(
          chess960invite,
          maxOpponentRating,
          minOpponentRating,
          noGuests,
          leaguePlay
        )
      );
    }
  }

  return (
    <table className={styles.lobbyTable}>
      <colgroup>
        <col width="34%" />
        <col width="22%" />
        <col width="22%" />
        <col width="22%" />
      </colgroup>
      <thead>
        <tr>
          <th>
            <div className="flex items-center space-x-1">
              <div className="text-orange-400">
                <FaUser />
              </div>
              <div className="hidden md:block">{t("Player")}</div>
            </div>
          </th>
          <th>
            <div className="flex items-center space-x-1">
              <div className="text-orange-400">
                <FaSignal />
              </div>
              <div className="hidden md:block">{t("Rating")}</div>
            </div>
          </th>
          <th>
            <div className="flex items-center space-x-1">
              <div className="text-orange-400">
                <FaClock />
              </div>
              <div className="hidden md:block">{lobbyT("Time")}</div>
            </div>
          </th>
          <th>
            <div className="flex items-center space-x-1">
              <div className="text-orange-400">
                <FaChessRook />
              </div>
              <div className="hidden md:block">{lobbyT("Mode")}</div>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>{allRows}</tbody>
    </table>
  );
}

export function acceptInvite(invite, leaguePlay) {
  const inv = {
    id: invite.id,
    leaguePlay,
    time: invite.time,
    inc: invite.inc,
    variant: invite.variant,
  };
  LobbyWs.acceptInvite(inv);
}

export default function JoinGame() {
  const { wsConnected } = useWsConnection();

  // const { t } = useTranslation("common");
  // const { t: lobbyT } = useTranslation("lobby");

  // const { leaguePlay, invites, gamesCreated } = useLobby();
  // const { maxOpponentRating, minOpponentRating, noGuests } = useSettings();
  // const { displayName } = useUser();
  // const getElo = useElo();

  // const { wsConnected } = useWsConnection();

  return (
    <div className={styles.joinGame}>
      <div className={styles.lobbyTableCont}>
        {wsConnected ? (
          <InvitesTable />
        ) : (
          <div className="flex justify-center items-center h-full">
            <span className="loader"></span>
          </div>
        )}
      </div>
    </div>
  );
}
